import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { CountBadge } from './Badge'
import { cn } from './cn'

export type IconButtonSize = 32 | 36 | 40 | 'sm' | 'md' | 'lg'

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Required: icon buttons have no visible text. */
  'aria-label': string
  icon?: ReactNode
  size?: IconButtonSize
  variant?: 'ghost' | 'secondary' | 'primary' | 'danger'
  /** Optional red count badge at the top right ("9+" cap by default). */
  count?: number
  countMax?: number
  active?: boolean
  ref?: Ref<HTMLButtonElement>
}

const sizeMap: Record<string, string> = {
  // Phones get at least 40 px tap targets (UI.md 10); the icon stays the same size.
  '32': 'size-8 max-sm:size-10 [&_svg]:size-4',
  '36': 'size-9 max-sm:size-10 [&_svg]:size-[18px]',
  '40': 'size-10 [&_svg]:size-5',
}
const alias: Record<string, string> = { sm: '32', md: '36', lg: '40' }

const variants = {
  ghost: 'text-muted hover:bg-surface-2 hover:text-text data-[active=true]:bg-surface-2 data-[active=true]:text-text',
  secondary: 'border border-line-strong bg-surface text-text hover:bg-surface-2',
  primary: 'bg-primary text-on-primary hover:bg-primary-hover',
  danger: 'text-danger hover:bg-danger-tint',
}

/** Square icon only button (32, 36 or 40) with an optional unread count. */
export function IconButton({
  icon,
  size = 36,
  variant = 'ghost',
  count,
  countMax = 9,
  active,
  className,
  children,
  type,
  ref,
  ...rest
}: IconButtonProps) {
  const key = alias[String(size)] ?? String(size)
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      data-active={active || undefined}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-control transition-[color,background-color,border-color,box-shadow] duration-150 ease-out',
        'focus-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0 [&_svg]:stroke-[1.75]',
        sizeMap[key] ?? sizeMap['36'],
        variants[variant],
        className,
      )}
      {...rest}
    >
      {icon ?? children}
      {count !== undefined && count > 0 && (
        <CountBadge count={count} max={countMax} className="pointer-events-none absolute -top-1 -right-1 ring-2 ring-surface" />
      )}
    </button>
  )
}
